export class RoundedCornersPlugin {
  private tinymce: any;

  constructor(tinymce: any) {
    this.tinymce = tinymce;
  }

  create() {
    const self = this;

    this.tinymce.PluginManager.add('rounded-corners', function (editor: any, url: string) {
      // Add toolbar button
      editor.ui.registry.addToggleButton('rounded-corners', {
        icon: 'border-width',
        tooltip: 'Toggle rounded corners',
        onAction: function (api: any) {
          self.toggleRoundedCorners(editor);
          api.setActive(self.hasRoundedCorners(editor));
        },
        onSetup: function (api: any) {
          // Set initial state
          api.setActive(self.hasRoundedCorners(editor));

          // Update state when content changes
          const updateState = () => {
            api.setActive(self.hasRoundedCorners(editor));
          };

          editor.on('NodeChange SetContent', updateState);

          return function () {
            editor.off('NodeChange SetContent', updateState);
          };
        },
      });

      // Add menu item
      editor.ui.registry.addMenuItem('rounded-corners', {
        text: 'Rounded Corners',
        onAction: function () {
          self.toggleRoundedCorners(editor);
        },
      });

      // Setup command
      editor.addCommand('mceRoundedCorners', function (ui: boolean, state: boolean) {
        if (state) {
          self.applyRoundedCorners(editor);
        } else {
          self.removeRoundedCorners(editor);
        }
      });

      // Initialize on editor init
      editor.on('init', function () {
        setTimeout(() => {
          self.initializeRoundedCorners(editor);
        }, 100);
      });
    });
  }

  private toggleRoundedCorners(editor: any) {
    if (this.hasRoundedCorners(editor)) {
      this.removeRoundedCorners(editor);
    } else {
      this.applyRoundedCorners(editor);
    }
  }

  private applyRoundedCorners(editor: any) {
    // Store state
    editor.hasRoundedCorners = true;

    // Apply to content
    const content = editor.getContent();
    if (content && content.trim()) {
      this.updateContentRoundedCorners(editor, true);
    }

    // Trigger change event
    editor.fire('Change');
  }

  private removeRoundedCorners(editor: any) {
    // Store state
    editor.hasRoundedCorners = false;

    // Remove from content
    const content = editor.getContent();
    if (content && content.trim()) {
      this.updateContentRoundedCorners(editor, false);
    }

    // Trigger change event
    editor.fire('Change');
  }

  private updateContentRoundedCorners(editor: any, apply: boolean) {
    // Prevent recursive calls
    if (editor._updatingRoundedCorners) {
      return;
    }

    const content = editor.getContent();
    if (content && content.trim()) {
      editor._updatingRoundedCorners = true;

      try {
        // Create a temporary DOM element to parse and modify the content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;

        const firstChild = tempDiv.firstElementChild;
        if (firstChild) {
          // Update or remove border-radius from the root element
          const existingStyle = firstChild.getAttribute('style') || '';

          // Remove existing border-radius
          let updatedStyle = existingStyle.replace(/border-radius:\s*[^;]+;?/gi, '').trim();

          if (apply) {
            // Add border-radius
            if (updatedStyle && !updatedStyle.endsWith(';')) {
              updatedStyle += ';';
            }
            updatedStyle += ' border-radius: 6px;';
          }

          firstChild.setAttribute('style', updatedStyle);

          const updatedContent = tempDiv.innerHTML;
          if (updatedContent !== content) {
            editor.setContent(updatedContent, { format: 'html' });
          }
        } else if (content.trim() !== '' && apply) {
          // If no root element and we want to apply, wrap content in a div with border-radius
          const wrappedContent = `<div style="border-radius: 6px;">${content}</div>`;
          editor.setContent(wrappedContent, { format: 'html' });
        }
      } finally {
        // Always clear the flag
        setTimeout(() => {
          editor._updatingRoundedCorners = false;
        }, 10);
      }
    }
  }

  private hasRoundedCorners(editor: any): boolean {
    // Check stored state first
    if (editor.hasRoundedCorners !== undefined) {
      return editor.hasRoundedCorners;
    }

    // Check content for border-radius
    const content = editor.getContent();
    if (content && content.trim()) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;

      const firstChild = tempDiv.firstElementChild;
      if (firstChild) {
        const style = firstChild.getAttribute('style');
        if (style) {
          return /border-radius:\s*[^;]+/i.test(style);
        }
      }
    }
    return false;
  }

  private initializeRoundedCorners(editor: any) {
    // Extract rounded corners state from content if it exists
    const hasRounded = this.hasRoundedCorners(editor);
    editor.hasRoundedCorners = hasRounded;
  }

  // Public method to get rounded corners state
  public getRoundedCornersState(editor: any): boolean {
    return this.hasRoundedCorners(editor);
  }

  // Public method to set rounded corners programmatically
  public setRoundedCorners(editor: any, state: boolean) {
    if (state) {
      this.applyRoundedCorners(editor);
    } else {
      this.removeRoundedCorners(editor);
    }
  }
}
